// src/services/authService.ts
// Authentication service with SSO support

import axios from 'axios';
import * as jose from 'jose';
import { getCookie, setCookie, deleteCookie, clearAuthCookies } from '../utils/cookieUtils';
import { clearFacilityFromStorage } from '../utils/facilityStorage';
import api from './axiosConfig';

const WRITE_API_URL = process.env.REACT_APP_FLUX_API_URL;

// ============================================
// Types
// ============================================

export interface User {
  id: number | string;
  email: string;
  name?: string;
  roles: string[];
  canAccessUserManagement: boolean;
  pages: string[];
  account_id?: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  email: string;
  id: number | string;
  roles: string[];
  pages: string[];
  username?: string;
}

export interface CheckAuthMethodResponse {
  auth_type: 'sso' | 'password';
  provider?: string;
  redirect_url?: string;
}

export interface RefreshTokenResponse {
  token: string;
  status: 'success';
}

// ============================================
// Token Management
// ============================================

/**
 * Set the auth token in cookie and axios headers
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    const { exp } = jose.decodeJwt(token);
    const expiry = new Date((exp as number) * 1000).toUTCString();
    setCookie('authToken', token, { expires: expiry });
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

/**
 * Set the auth header only (when cookie is already set by backend)
 */
export function setAuthHeader(token: string | null): void {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

/**
 * Get the current auth token from cookie
 */
export function getAuthToken(): string | null {
  return getCookie('authToken');
}

/**
 * Check if current token is expired or about to expire
 */
export function isTokenExpired(token: string, bufferSeconds: number = 120): boolean {
  try {
    const { exp } = jose.decodeJwt(token);
    if (!exp) return true;
    const expiryTime = (exp as number) * 1000;
    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;
    return (expiryTime - now) < bufferMs;
  } catch {
    return true;
  }
}

/**
 * Decode JWT and extract claims
 */
export function decodeToken(token: string): jose.JWTPayload | null {
  try {
    return jose.decodeJwt(token);
  } catch {
    return null;
  }
}

// ============================================
// Auth Method Detection (SSO)
// ============================================

/**
 * Check authentication method for an email address.
 * Returns whether user should use SSO or password login.
 */
export async function checkAuthMethod(email: string): Promise<CheckAuthMethodResponse> {
  const response = await axios.post<CheckAuthMethodResponse>(
    `${WRITE_API_URL}/admin/check_auth_method`,
    { email: email.toLowerCase().trim() }
  );
  return response.data;
}

// ============================================
// Login Functions
// ============================================

/**
 * Login with email and password (existing flow)
 */
export async function login(credentials: LoginCredentials): Promise<User> {
  const response = await axios.post<LoginResponse>(
    `${WRITE_API_URL}/admin/login`,
    credentials,
    { withCredentials: true }
  );

  const { token, email, id, roles, pages, username } = response.data;

  const user: User = {
    id,
    name: username || '',
    roles: roles || ['admin'],
    email,
    pages: pages || [],
    canAccessUserManagement: false
  };

  setAuthToken(token);

  // Only check user management access for non-carrier users
  const isCarrier = roles && roles.some(role => role.toLowerCase() === 'carrier');
  if (!isCarrier) {
    try {
      const userManagementResponse = await axios.get(
        `${WRITE_API_URL}/admin/can_access_user_management`
      );
      user.canAccessUserManagement = userManagementResponse.data.result;
    } catch (error) {
      console.error('Could not check user management access:', error);
      user.canAccessUserManagement = false;
    }
  }

  localStorage.setItem('user', JSON.stringify(user));

  return user;
}

/**
 * Initiate SSO login by redirecting to IdP
 */
export function initiateSSO(redirectUrl: string): void {
  window.location.href = redirectUrl;
}

// ============================================
// Token Refresh (SSO)
// ============================================

/**
 * Cross-tab refresh coordination via BroadcastChannel.
 * Only one tab sends the actual POST /admin/refresh_token; other tabs
 * wait for the result over the channel. Falls back to direct request
 * if BroadcastChannel is unsupported or the leader tab doesn't respond.
 */
const REFRESH_CHANNEL_NAME = 'freightroll_token_refresh';
const CROSS_TAB_TIMEOUT_MS = 5000;

type RefreshMessage =
  | { type: 'refresh_start'; nonce: string }
  | { type: 'refresh_done'; nonce: string; token: string }
  | { type: 'refresh_failed'; nonce: string; error: string };

let _refreshChannel: BroadcastChannel | null = null;
try {
  _refreshChannel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
} catch {
  // BroadcastChannel not supported — single-tab mutex still works
}

/** In-memory mutex for same-tab dedup */
let _refreshPromise: Promise<string> | null = null;

/** Set when another tab announced it's refreshing */
let _crossTabPromise: Promise<string> | null = null;

function waitForCrossTabRefresh(nonce: string): Promise<string> {
  if (!_refreshChannel) return Promise.reject(new Error('no channel'));

  return new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('cross_tab_timeout'));
    }, CROSS_TAB_TIMEOUT_MS);

    function handler(event: MessageEvent<RefreshMessage>) {
      const msg = event.data;
      if (msg.nonce !== nonce) return; // different refresh cycle

      cleanup();
      if (msg.type === 'refresh_done') {
        setAuthToken(msg.token);
        resolve(msg.token);
      } else if (msg.type === 'refresh_failed') {
        reject(new Error(msg.error));
      }
    }

    function cleanup() {
      clearTimeout(timeout);
      _refreshChannel?.removeEventListener('message', handler as EventListener);
      _crossTabPromise = null;
    }

    _refreshChannel!.addEventListener('message', handler as EventListener);
  });
}

/**
 * Refresh the access token using the httpOnly refresh token cookie.
 *
 * Coordination layers (in order):
 * 1. Same-tab: _refreshPromise deduplicates concurrent callers within one tab
 * 2. Cross-tab: BroadcastChannel ensures only one tab sends the HTTP request
 */
export async function refreshAccessToken(): Promise<string> {
  // Same-tab dedup
  if (_refreshPromise) {
    return _refreshPromise;
  }

  // Cross-tab dedup: if another tab is already refreshing, wait for it
  if (_crossTabPromise) {
    return _crossTabPromise;
  }

  const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  _refreshPromise = (async () => {
    // Announce to other tabs that we're taking the lead
    _refreshChannel?.postMessage({ type: 'refresh_start', nonce } as RefreshMessage);

    try {
      const response = await axios.post<RefreshTokenResponse>(
        `${WRITE_API_URL}/admin/refresh_token`,
        {},
        { withCredentials: true }
      );

      const newToken = response.data.token;
      // Update both the cookie and axios header so all request paths
      // (axios interceptor + graphqlFetch) use the new token immediately
      setAuthToken(newToken);

      // Broadcast success to other tabs
      _refreshChannel?.postMessage({ type: 'refresh_done', nonce, token: newToken } as RefreshMessage);

      return newToken;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'unknown';
      _refreshChannel?.postMessage({ type: 'refresh_failed', nonce, error: errorMsg } as RefreshMessage);
      throw err;
    }
  })();

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

/**
 * Handle incoming refresh_start from another tab. If we're not already
 * refreshing, wait for that tab's result instead of starting our own.
 */
if (_refreshChannel) {
  _refreshChannel.addEventListener('message', ((event: MessageEvent<RefreshMessage>) => {
    const msg = event.data;
    if (msg.type === 'refresh_start' && !_refreshPromise && !_crossTabPromise) {
      _crossTabPromise = waitForCrossTabRefresh(msg.nonce);
      // Auto-clear on settle
      _crossTabPromise.catch(() => {}).finally(() => { _crossTabPromise = null; });
    }
  }) as EventListener);
}

// ============================================
// Logout
// ============================================

/**
 * Logout from all sessions.
 * Calls backend to invalidate refresh tokens, then clears client state.
 * Returns the IdP SLO redirect URL if the user was logged in via SSO,
 * so the caller can redirect the browser to terminate the IdP session.
 */
let _logoutPromise: Promise<string | null> | null = null;

export async function logoutWithApi(): Promise<string | null> {
  if (_logoutPromise) return _logoutPromise;

  _logoutPromise = (async () => {
    const token = getAuthToken();
    let sloRedirectUrl: string | null = null;

    // Call backend to invalidate all refresh tokens
    if (token) {
      try {
        const response = await api.post('/admin/logout');
        sloRedirectUrl = response.data?.slo_redirect_url || null;
      } catch (error) {
        // Log but don't block logout on API failure
        console.error('Logout API call failed:', error);
      }
    }

    // Clear client-side state
    clearAuthState();

    return sloRedirectUrl;
  })();

  try {
    return await _logoutPromise;
  } finally {
    _logoutPromise = null;
  }
}

/**
 * Clear all client-side auth state (used by logout and on auth failure)
 * Synchronous version for immediate cleanup
 */
export function clearAuthState(): void {
  // Clear cookies
  clearAuthCookies();

  // Clear localStorage
  localStorage.removeItem('user');
  clearFacilityFromStorage();
  localStorage.removeItem('yardManagement_zoneFilter');
  localStorage.removeItem('yardManagement_spotBatch');
  localStorage.removeItem('yardManagement_showUnallocated');

  // Clear axios header
  setAuthHeader(null);
}

/**
 * Legacy logout function for backward compatibility
 * Clears client-side state without API call
 */
export function logout(): void {
  deleteCookie('authToken');
  localStorage.removeItem('user');
  clearFacilityFromStorage();

  // Clear yard management preferences
  localStorage.removeItem('yardManagement_zoneFilter');
  localStorage.removeItem('yardManagement_spotBatch');
  localStorage.removeItem('yardManagement_showUnallocated');

  setAuthToken(null);
}

// ============================================
// Session Validation
// ============================================

/**
 * Check if user has a valid session (token exists and not expired)
 */
export function hasValidSession(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  return !isTokenExpired(token, 0); // No buffer, just check if expired
}

/**
 * Check if token should be refreshed (exists but expiring soon)
 */
export function shouldRefreshToken(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  return isTokenExpired(token, 3600); // 1 hour buffer
}

// ============================================
// User Management Access (existing)
// ============================================

export async function checkUserManagementAccess(email: string): Promise<boolean> {
  try {
    const response = await axios.post(
      `${WRITE_API_URL}/admin/check_user_management_access`,
      { email }
    );
    return response.data?.can_access === true;
  } catch {
    return false;
  }
}

// ============================================
// Validate Token (placeholder - existing)
// ============================================

export async function validateToken(token: string): Promise<boolean> {
  // Placeholder for token validation
  // Currently relies on API calls to validate
  return true;
}

// ============================================
// Current User (SSO)
// ============================================

// Re-export from hook for convenience
export { fetchCurrentUser } from '../hooks/useCurrentUser';
export type { CurrentUser, Permissions, Facility as UserFacility } from '../hooks/useCurrentUser';
